import {
  Button,
  Checkbox,
  Dialog,
  HSpacer,
  Input,
  MenuItem,
  NumericInput,
  Radio,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { RewardsConstants, ToastMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { OrderTransactionsApi } from "@/utilities/api/OrderTransactionsApi";
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { UserApi } from '@/utilities/api/UserApi';
import { getOrderLabel, getOrderProductLabel } from '@/utilities/OrderLabels';
import { OrderTransactionEndpoint } from "@api/endpoints/OrderTransactionEndpoint";
import { ApiPromotion, ApiSubwalletBalance, ApiUser } from '@api/interfaces';
import { ApiOrderTransactionProduct } from '@api/interfaces/ApiOrderTransactionProduct';
import { ApiOrderTransaction } from "@api/interfaces/ApiOrderTransaction";
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import HighlightOff from '@mui/icons-material/HighlightOff';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Autocomplete, CircularProgress, RadioGroup, Stack, Tooltip } from '@mui/material';
import { TransactionType } from '@shared/enums';
import { SharedConfig } from '@shared/SharedConfig';
import { removeWhiteSpace } from '@shared/utilities';
import { getAccountName } from '@shared/utilities/UserUtilities';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import React, { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

type UpdatePointsDialogProps = {
  farmer: ApiUser,
  setShow: Dispatch<SetStateAction<boolean>>,
  show: boolean,
}

type PointsSource = 'rewards-program' | 'promotion';

const PositiveTransactionTypeOptions = [
  TransactionType.CREDIT,
  TransactionType.REDEMPTION_REFUND,
  TransactionType.RETURN_CORRECTION,
];

const NegativeTransactionTypeOptions = [
  TransactionType.RETURN,
  TransactionType.DEBIT,
  TransactionType.CORRECTION,
];

export const UpdatePointsDialog = ({
  farmer,
  setShow,
  show,
}: UpdatePointsDialogProps) => {
  const { t, i18n } = useTranslation();
  const [points, setPoints] = useState<number>();
  const [selectedOrder, setSelectedOrder] = useState<ApiOrderTransaction | string>();
  const [selectedOrderProduct, setSelectedOrderProduct] = useState<ApiOrderTransactionProduct>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [transactionType, setTransactionType] = useState<TransactionType>(TransactionType.CREDIT);
  const [pointsSource, setPointsSource] = useState<PointsSource>();
  const [selectedPromotion, setSelectedPromotion] = useState<ApiPromotion>();
  const [selectedProgram, setSelectedProgram] = useState<ApiRewardsProgram>();
  const [reasonDescription, setReasonDescription] = useState('');
  const [isExternalDescription, setIsExternalDescription] = useState(false);
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { isLoading: isAdjustingPoints, mutateAsync: manualPointsAdjustment  } = useMutation(
    async () => {
      let associatedOrderTransactionId : string | undefined = undefined;
      if (typeof selectedOrder !== 'string') {
        if (selectedOrder && 'externalId' in selectedOrder) {
          associatedOrderTransactionId = selectedOrder.id;
        }
      }

      await UserApi.manualPointsEdit(farmer.id, {
        amount: points ?? 0,
        orderId: associatedOrderTransactionId,
        orderTransactionProductId: associatedOrderTransactionId
          ? selectedOrderProduct?.id
          : undefined,
        description: reasonDescription || null,
        isDescriptionExternal: reasonDescription ? isExternalDescription : false,
        promotionId: selectedPromotion?.id,
        rewardsProgramId: selectedProgram?.id,
        transactionType,
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.LIST_SUBWALLET_BALANCES);
        openSnackbar(ToastMessages.addFarmerRewards);
      },
      onError: (error: Error) => {
        openSnackbar(error.message || t($ => $.common.error.genericError));
      },
    },
  );

  const { data: retailer } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, farmer.retailerId],
    () => RetailerApi.get(farmer.retailerId!),
  );

  const { data: balances } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, farmer.id],
    () => UserApi.getSubwalletBalances(farmer.id),
  );

  const { data: programs } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, farmer.retailerId],
    () => RewardsProgramApi.list({
      retailerId: farmer.retailerId!,
    }),
  );

  useEffect(() => {
    if (pointsSource === 'rewards-program' && programs && programs.length === 1) {
      setSelectedProgram(programs[0]);
    }
  }, [pointsSource, programs]);

  const { data: subwallets } = useQuery(
    [QueryKeys.LIST_SUBWALLETS, farmer.retailerId],
    () => SubwalletApi.list({ retailerId: farmer.retailerId! }),
    { enabled: !!farmer.retailerId },
  );

  const { data: promotions } = useQuery(
    [QueryKeys.GET_PROMOTIONS_FOR_RETAILER, farmer.retailerId],
    async () => PromotionApi.listForRetailer({
      includeExpired: true,
      retailerId: farmer.retailerId!,
    }),
  );

  useEffect(() => {
    if (pointsSource === 'promotion' && promotions?.data.length === 1) {
      setSelectedPromotion(promotions.data[0]);
    }
  }, [pointsSource, promotions]);

  const formatProgramPeriodDateRange = (program: ApiRewardsProgram) => (
    removeWhiteSpace(`
      ${DateTime.fromISO(program.startDate).toFormat('MMM dd, yyyy')} -
      ${DateTime.fromISO(program.endDate).toFormat('MMM dd, yyyy')}
    `)
  );

  const getSelectedSubwallet = useCallback(() => {
    let subwalletId: string | null = null;
    if (selectedProgram) {
      subwalletId = retailer?.programSubwalletId ?? null;
    } else if (selectedPromotion) {
      subwalletId = selectedPromotion.subwalletId;
    }

    if (subwalletId) {
      return subwallets?.find((subwallet) => subwallet.id === subwalletId) ?? null;
    }
  }, [selectedProgram, retailer, selectedPromotion, subwallets]);

  const getAssociatedExpirationDate = useCallback(() => {
    if (selectedProgram) {
      return selectedProgram.pointsExpirationDate;
    }
    return selectedPromotion?.pointsExpirationDate ?? null;
  }, [selectedProgram, selectedPromotion]);

  const getSelectedSubwalletBalance = useCallback(() => {
    const subwallet = getSelectedSubwallet();
    let matchingBalances: ApiSubwalletBalance[] = [];
    if (subwallet) {
      matchingBalances = (balances ?? [])
        .filter((balance) =>  balance.subwalletId === subwallet.id);
    }
    return matchingBalances.reduce((acc, balance) => balance.points + acc, 0);
  }, [
    balances,
    getSelectedSubwallet,
  ]);

  const getPromotionProgram = () => {
    if (selectedPromotion) {
      const promotion = promotions?.data.find(
        (promotion) => promotion.id === selectedPromotion.id,
      );
      if (promotion) {
        if (promotion.rewardsProgramId) {
          const rewardsProgramById = programs?.find(
            (program) => program.id === promotion.rewardsProgramId,
          );
          return rewardsProgramById ?? null;
        }
        const rewardsProgram = programs?.find((program) => (
          promotion.startDate >= program.startDate && promotion.endDate <= program.endDate
        ));
        return rewardsProgram ?? null;
      }
    }
    return null;
  };

  const onSave = async () => {
    await manualPointsAdjustment();
    setShow(false);
    setShowConfirmDialog(false);
  };

  const isNegativeBalance = () => {
    if (transactionType && points && NegativeTransactionTypeOptions.includes(transactionType)) {
      return getSelectedSubwalletBalance() - points < 0;
    }
    return false;
  };

  const orderTransactionsQuery : OrderTransactionEndpoint.List.Query = {
    farmerId: farmer.id,
    limit: SharedConfig.maxPageLimit,
  };

  const { data: orderTransactions, isFetching: isFetchingOrders } = useQuery(
    [QueryKeys.GET_ORDERS, orderTransactionsQuery],
    () => OrderTransactionsApi.list(orderTransactionsQuery),
  );

  const orderOptions = [
    farmer.externalId ? 'N/A' : 'Growers',
    ...(orderTransactions?.data ?? []),
  ];

  const selectedOrderId = typeof selectedOrder === 'object' ? selectedOrder.id : undefined;

  const { data: selectedOrderDetails, isFetching: isFetchingOrderProducts } = useQuery(
    [QueryKeys.GET_ORDER, selectedOrderId],
    () => OrderTransactionsApi.getOrder(selectedOrderId!),
    { enabled: !!selectedOrderId },
  );

  const orderProductOptions = (selectedOrderDetails?.products ?? [])
    .filter(({ product }) => !product.isLoyaltySku);

  const isFormValid = !!points
    && !!transactionType
    && !!getSelectedSubwallet()
    && ((pointsSource === 'rewards-program' && selectedProgram)
      || (pointsSource === 'promotion' && selectedPromotion)
    );

  return (
    <Dialog
      acceptButton={() => (
        <Button
          disabled={!isFormValid}
          loading={isAdjustingPoints}
          onClick={() => setShowConfirmDialog(true)}
          testID='save-rewards-button'
          variant='contained'
        >
          {t($ => $.common.actions.save)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button
          onClick={() => setShow(false)}
          {...props}
          color='inherit'
          testID="rewards-cancel-button"
        >
          {t($ => $.common.actions.cancel)}
        </Button>
      )}
      onClose={() => setShow(false)}
      open={show}
      showCloseButton={false}
      testID="update-points-dialog"
      title={t($ => $.customers.updatePoints.title)}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack maxWidth="60%">
          <Text category="label-medium">
            {t($ => $.customers.updatePoints.customerAccount)}
          </Text>
          <Text category="body-medium">
            {getAccountName(farmer)}
          </Text>
        </Stack>
      </Stack>
      <VSpacer size="7" />
      <Text category="body-medium">
        {t($ => $.customers.updatePoints.pointsAssociatedTo)}
      </Text>
      <VSpacer size="3" />
      <RadioGroup
        name="point-association-radio-group"
        onChange={(e) => {
          setPointsSource(e.target.value as PointsSource);
          setSelectedProgram(undefined);
          setSelectedPromotion(undefined);
        }}
        row
        value={pointsSource}
      >
        <Radio testID="rewards-program-radio" value="rewards-program">
          {t($ => $.customers.updatePoints.rewardsProgram)}
        </Radio>
        <Radio testID="promotion-radio" value="promotion">
          {t($ => $.customers.updatePoints.promotion)}
        </Radio>
      </RadioGroup>
      {!!pointsSource && (
        <>
          {pointsSource === 'rewards-program' && (
            <>
              <VSpacer size="3" />
              <Select
                label={t($ => $.customers.updatePoints.selectedProgramPeriod)}
                onChangeValue={(programId) => {
                  const program = programs?.find((program) => program.id === programId);
                  if (program) {
                    setSelectedProgram(program);
                  }
                }}
                testID="rewards-program-select"
                value={selectedProgram?.id}
              >
                {sortBy(programs ?? [], 'startDate').map((program) => (
                  <MenuItem
                    key={program.id}
                    testID={`${program.id}-option`}
                    value={program.id}
                  >
                    {formatProgramPeriodDateRange(program)}
                  </MenuItem>
                ))}
              </Select>
              {!!selectedProgram && !!retailer && !retailer.programSubwalletId && (
                <>
                  <VSpacer size="3" />
                  <Text category="body-small" color="error">
                    {t($ => $.customers.updatePoints.noSubwalletError)}
                  </Text>
                </>
              )}
            </>
          )}
          {pointsSource === 'promotion' && (
            <>
              <VSpacer size="3" />
              <Select
                label={t($ => $.customers.updatePoints.selectPromotion)}
                onChangeValue={(promotionId) => {
                  const promotion = promotions?.data.find(
                    (promotion) => promotion.id === promotionId,
                  );
                  if (promotion) {
                    setSelectedPromotion(promotion);
                  }
                }}
                testID="promotion-select"
                value={selectedPromotion?.id}
              >
                {sortBy(promotions?.data ?? [], 'name').map((promotion) => (
                  <MenuItem
                    key={promotion.id}
                    testID={`${promotion.name}-option`}
                    value={promotion.id}
                  >
                    {promotion.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </>
      )}
      {!!getSelectedSubwallet() && (
        <>
          <VSpacer size="3" />
          <Text category="body-medium">
            {t($ => $.customers.updatePoints.subwallet, { name: getSelectedSubwallet()?.name })}
          </Text>
          <VSpacer size="3" />
          <Text category="body-medium">
            {t($ => $.customers.updatePoints.currentBalance,
              { amount: getSelectedSubwalletBalance() },
            )}
          </Text>
        </>
      )}
      {!!selectedPromotion && (
        <>
          <VSpacer size="3" />
          <Text category="body-medium">
            {t($ => $.customers.updatePoints.programPeriod, {
              period: getPromotionProgram()
                && formatProgramPeriodDateRange(getPromotionProgram()!),
            })}
          </Text>
        </>
      )}
      {!!(selectedPromotion || selectedProgram) && (
        <>
          <VSpacer size="3" />
          <Text category="body-medium">
            {t($ => $.customers.updatePoints.pointsExpirationDate)}:{' '}
            {getAssociatedExpirationDate()
              ? DateTime.fromISO(getAssociatedExpirationDate()!).toFormat('MMM dd, yyyy')
              : t($ => $.customers.updatePoints.notApplicable)}
          </Text>
          <VSpacer size="7" />
          <Autocomplete
            clearIcon={<HighlightOff />}
            disablePortal
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              if ('publicId' in option) {
                return t($ => $.customers.updatePoints.requestNumber, {
                  publicId: option.publicId,
                });
              }
              return getOrderLabel(option, t);
            }}
            loading={isFetchingOrders}
            onChange={(_, value) => {
              setSelectedOrder(value ?? undefined);
              setSelectedOrderProduct(undefined);
            }}
            options={orderOptions}
            renderInput={(params) => (
              <Input
                label={t($ => $.customers.updatePoints.selectOrderOptional)}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {isFetchingOrders ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
                testID="order-autocomplete-input"
              />
            )}
          />
          {!!selectedOrderId && (
            <>
              <VSpacer size="7" />
              <Autocomplete
                clearIcon={<HighlightOff />}
                disablePortal
                getOptionLabel={(option) => getOrderProductLabel(option, i18n)}
                loading={isFetchingOrderProducts}
                onChange={(_, value) => {
                  setSelectedOrderProduct(value ?? undefined);
                }}
                options={orderProductOptions}
                renderInput={(params) => (
                  <Input
                    label={t($ => $.customers.updatePoints.selectLineItemOptional)}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Fragment>
                          {isFetchingOrderProducts ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </Fragment>
                      ),
                    }}
                    testID="order-product-autocomplete-input"
                  />
                )}
                value={selectedOrderProduct ?? null}
              />
            </>
          )}
          <VSpacer size="7" />
          <Input
            label={t($ => $.customers.updatePoints.reasonDescriptionOptional)}
            onChangeText={setReasonDescription}
            placeholder={t($ => $.customers.updatePoints.descriptionPlaceholder)}
            testID="reason-description-input"
            value={reasonDescription}
          />
          <VSpacer size="5" />
          <Stack alignItems="center" direction="row">
            <Checkbox
              checked={isExternalDescription}
              onChangeChecked={setIsExternalDescription}
              testID="external-description-checkbox"
            >
              <Text category="body-medium">
                {t($ => $.customers.updatePoints.externalDescriptionLabel)}
              </Text>
            </Checkbox>
            <HSpacer size="2" />
            <Tooltip title={t($ => $.customers.updatePoints.externalDescriptionTooltip)}>
              <InfoOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
            </Tooltip>
          </Stack>
          <VSpacer size="7" />
          <Text category="body-xlarge">
            {t($ => $.customers.updatePoints.enterValue)}
          </Text>
          <Stack direction="row" justifyContent="space-between">
            <Select
              label={t($ => $.customers.updatePoints.type)}
              onChangeValue={(transactionType) => {
                setTransactionType(transactionType as TransactionType);
              }}
              testID="transaction-type-select"
              value={transactionType}
            >
              {[
                ...PositiveTransactionTypeOptions,
                ...NegativeTransactionTypeOptions,
              ].map((option: string) => (
                <MenuItem
                  key={option}
                  testID={`${option}-transaction-type`}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
            <HSpacer size="5" />
            <NumericInput
              maxValue={RewardsConstants.addRewardsMaxLimit}
              minValue={0}
              onChangeNumber={setPoints}
              placeholder={
                `${transactionType && NegativeTransactionTypeOptions.includes(transactionType)
                  ? '-' : ''}0.00`
              }
              prefix={transactionType && NegativeTransactionTypeOptions.includes(transactionType)
                ? '-' : ''}
              testID="add-rewards-points-input"
              value={points}
              width="220px"
            />
          </Stack>
          {isNegativeBalance() && (
            <>
              <VSpacer size="2" />
              <Text category="body-small" color="error">
                {t($ => $.customers.updatePoints.negativeBalanceWarning)}
              </Text>
            </>
          )}
          {isFormValid && (
            <>
              <VSpacer size="3" />
              <Text category="body-medium">
                {t($ => $.customers.updatePoints.resultsPreview)}
              </Text>
              <Stack direction="row">
                <Text category="body-small">
                  {t($ => $.customers.updatePoints.currentPoints)}:
                </Text>
                <HSpacer size="5" />
                <Stack textAlign="right">
                  <Text category="body-small">
                    {getSelectedSubwalletBalance()}
                  </Text>
                  <Text category="body-small">
                    {NegativeTransactionTypeOptions.includes(transactionType) ? '-' : '+'} {points}
                  </Text>
                  <Text category="body-small">
                    =
                  </Text>
                  <Text category="body-small">
                    {getSelectedSubwalletBalance()
                      + (NegativeTransactionTypeOptions.includes(transactionType)
                        ? -points : points)}
                  </Text>
                </Stack>
              </Stack>
            </>
          )}
        </>
      )}
      <ConfirmDialog
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={onSave}
        open={showConfirmDialog}
        testID="add-remove-points-confirm-dialog"
        title={t($ => $.customers.updatePoints.confirmTitle)}
      >
        {RewardsConstants.addRewardsConfirmation}
      </ConfirmDialog>
    </Dialog>
  );
};
